import React, { useContext, useState } from 'react';
import { ThemeContext } from '../hooks/ThemeContext';
import { UserContext } from '../hooks/UserContext';
import { Link } from 'react-router-dom';
import { 
  Palette, 
  TrendingUp, 
  Shield, 
  DollarSign, 
  Users, 
  Sparkles,
  CheckCircle,
  ArrowRight,
  Send,
  AlertCircle
} from 'lucide-react';
import { api } from '../services/baseUrl';

const DevenirCreateur = () => {
  const { theme } = useContext(ThemeContext);
  const { isAuthenticated, role } = useContext(UserContext);
  const isDark = theme === 'dark';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    portfolio: '',
    motivation: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Vérifier si l'utilisateur est créateur (role 2 = créateur, 3 = admin)
  const isCreator = role === 2 || role === 3;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await api.post('/creator/request', formData);
      
      if (response.data.success) {
        setSuccess(true);
        setFormData({ name: '', email: '', portfolio: '', motivation: '' });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    {
      icon: <DollarSign className="w-8 h-8" />,
      title: 'Commissions Équitables',
      desc: 'Gardez la majorité de vos revenus avec notre système de commission transparent.'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Audience Ciblée',
      desc: 'Accédez à une communauté de clients recherchant activement des créations de qualité.'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Protection Garantie',
      desc: 'Vos créations sont protégées avec filigrane et téléchargement sécurisé.'
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: 'Croissance Facilitée',
      desc: 'Outils et support pour développer votre activité et votre portfolio.'
    },
    {
      icon: <Sparkles className="w-8 h-8" />,
      title: 'Visibilité Maximale',
      desc: 'Mettez en avant vos créations auprès de milliers de clients potentiels.'
    },
    {
      icon: <CheckCircle className="w-8 h-8" />,
      title: 'Paiements Sécurisés',
      desc: 'Transactions sécurisées via Stripe, paiements rapides et fiables.'
    }
  ];

  const steps = [
    {
      number: '1',
      title: 'Créez votre compte',
      desc: 'Inscrivez-vous gratuitement en tant que créateur sur notre plateforme.'
    },
    {
      number: '2',
      title: 'Configurez votre profil',
      desc: 'Présentez votre travail et définissez vos tarifs.'
    },
    {
      number: '3',
      title: 'Uploadez vos créations',
      desc: 'Ajoutez vos bannières, logos, illustrations et autres créations digitales.'
    },
    {
      number: '4',
      title: 'Commencez à vendre',
      desc: 'Vos créations sont immédiatement disponibles à la vente sur notre marketplace.'
    }
  ];

  const creationTypes = [
    'Bannières YouTube, Twitch, Discord',
    'Logos et identités visuelles',
    'Illustrations digitales',
    'Templates réseaux sociaux',
    'Designs pour stream et gaming',
    'Assets graphiques personnalisés'
  ];

  return (
    <div className={`flex-1 flex flex-col ${isDark ? 'bg-[#0f1114] text-white' : 'bg-slate-50 text-gray-800'}`}>
      {/* Hero Section */}
      <div className={`${isDark ? 'bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20' : 'bg-gradient-to-br from-rose-50 via-purple-50 to-blue-50'} border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
        <div className="container mx-auto px-4 py-16 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Devenez <span className="text-rose-500">Créateur</span>
              </h1>
              <p className="text-lg md:text-xl opacity-90 mb-6">
                Rejoignez notre communauté de créateurs et vendez vos créations digitales sur une plateforme sécurisée et moderne.
              </p>
              <div className="flex flex-wrap gap-4">
                {isAuthenticated ? (
                  isCreator ? (
                    <Link 
                      to="/espace-createur" 
                      className="px-6 py-3 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors font-medium flex items-center gap-2"
                    >
                      Accéder à mon espace
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  ) : (
                    <div className={`p-6 rounded-xl border-2 w-full ${isDark ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-yellow-50 border-yellow-300'}`}>
                      <div className="flex items-start gap-3 mb-3">
                        <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <h3 className="font-semibold text-lg mb-1">Vous n'êtes pas encore créateur</h3>
                          <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            Remplissez le formulaire ci-dessous pour faire votre demande. Nous vous répondrons sous 48h.
                          </p>
                        </div>
                      </div>
                    </div>
                  )
                ) : (
                  <>
                    <Link 
                      to="/register" 
                      className="px-6 py-3 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors font-medium"
                    >
                      S'inscrire gratuitement
                    </Link>
                    <Link 
                      to="/login" 
                      className={`px-6 py-3 rounded-lg border transition-colors font-medium ${isDark ? 'border-white/20 hover:bg-white/10' : 'border-gray-300 hover:bg-white'}`}
                    >
                      Se connecter
                    </Link>
                  </>
                )}
              </div>
            </div>
            <div className={`hidden lg:block p-8 rounded-2xl ${isDark ? 'bg-white/5 border border-white/10' : 'bg-white border border-gray-200'}`}>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span>Inscription 100% gratuite</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span>Commissions transparentes</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span>Paiements sécurisés</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span>Support créateurs 24/7</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Avantages */}
      <div className="container mx-auto px-4 py-16 max-w-7xl">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-4">Pourquoi nous rejoindre ?</h2>
          <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Profitez d'une plateforme conçue pour maximiser vos ventes et votre visibilité
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {benefits.map((benefit, idx) => (
            <div 
              key={idx}
              className={`p-6 rounded-lg ${isDark ? 'bg-[#0b0b0c] border border-white/6' : 'bg-white border border-gray-100'} hover:shadow-lg transition-all`}
            >
              <div className={`inline-block p-3 rounded-lg mb-4 ${isDark ? 'bg-rose-500/20 text-rose-400' : 'bg-rose-100 text-rose-600'}`}>
                {benefit.icon}
              </div>
              <h3 className="text-xl font-semibold mb-2">{benefit.title}</h3>
              <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{benefit.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Comment ça marche */}
      <div className={`${isDark ? 'bg-[#0b0b0c]' : 'bg-white'} border-y ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
        <div className="container mx-auto px-4 py-16 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Comment ça marche ?</h2>
            <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              4 étapes simples pour commencer à vendre vos créations
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, idx) => (
              <div key={idx} className="text-center">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4 ${isDark ? 'bg-rose-500/20 text-rose-400' : 'bg-rose-100 text-rose-600'}`}>
                  {step.number}
                </div>
                <h3 className="text-lg font-semibold mb-2">{step.title}</h3>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Types de créations */}
      <div className="container mx-auto px-4 py-16 max-w-7xl">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-4">Que pouvez-vous vendre ?</h2>
          <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Une grande variété de créations digitales
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto mb-12">
          {creationTypes.map((type, idx) => (
            <div 
              key={idx}
              className={`p-4 rounded-lg ${isDark ? 'bg-[#0b0b0c] border border-white/6' : 'bg-white border border-gray-100'} flex items-center gap-3`}
            >
              <Palette className={`w-5 h-5 ${isDark ? 'text-rose-400' : 'text-rose-600'}`} />
              <span>{type}</span>
            </div>
          ))}
        </div>

        {/* CTA Final */}
        <div className={`p-8 rounded-xl ${isDark ? 'bg-gradient-to-r from-rose-500/20 to-purple-500/20 border border-white/10' : 'bg-gradient-to-r from-rose-50 to-purple-50 border border-gray-200'} text-center`}>
          <h3 className="text-2xl font-bold mb-4">Prêt à commencer ?</h3>
          <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            Rejoignez des centaines de créateurs qui font confiance à Krayne Agency
          </p>
          {!isAuthenticated && (
            <Link 
              to="/register" 
              className="inline-block px-8 py-4 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors font-medium text-lg"
            >
              Créer mon compte créateur gratuitement
            </Link>
          )}
        </div>
      </div>

      {/* Formulaire de demande - visible pour utilisateurs connectés non-créateurs */}
      {isAuthenticated && !isCreator && (
        <div className={`${isDark ? 'bg-[#0b0b0c]' : 'bg-white'} border-y ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
          <div className="container mx-auto px-4 py-16 max-w-3xl">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold mb-4">Faire ma demande de créateur</h2>
              <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Remplissez ce formulaire pour rejoindre notre communauté de créateurs
              </p>
            </div>

            {success ? (
              <div className={`p-8 rounded-xl text-center ${isDark ? 'bg-green-500/10 border border-green-500/30' : 'bg-green-50 border border-green-300'}`}>
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-2xl font-bold mb-2 text-green-600">Demande envoyée avec succès !</h3>
                <p className={`${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Nous avons bien reçu votre demande. Notre équipe l'examinera et vous contactera sous 48h à l'adresse email fournie.
                </p>
                <div className={`mt-6 p-4 rounded-lg ${isDark ? 'bg-white/5' : 'bg-white'}`}>
                  <h4 className="font-semibold mb-3">📊 Répartition des revenus :</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Créateur</span>
                      <span className="font-bold text-green-600">75%</span>
                    </div>
                    <div className="flex justify-between">
                      <span>URSSAF (charges sociales)</span>
                      <span className="font-bold text-red-600">20%</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Krayne Agency (commission)</span>
                      <span className="font-bold text-yellow-600">5%</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className={`p-4 rounded-lg ${isDark ? 'bg-red-500/10 border border-red-500/30 text-red-400' : 'bg-red-50 border border-red-300 text-red-700'}`}>
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-5 h-5" />
                      <span>{error}</span>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Nom complet <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className={`w-full px-4 py-3 rounded-lg border ${isDark ? 'bg-[#0f1114] border-white/10 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
                      placeholder="Votre nom et prénom"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Email <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className={`w-full px-4 py-3 rounded-lg border ${isDark ? 'bg-[#0f1114] border-white/10 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
                      placeholder="votre@email.com"
                    />
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Portfolio / Site web
                  </label>
                  <input
                    type="url"
                    name="portfolio"
                    value={formData.portfolio}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg border ${isDark ? 'bg-[#0f1114] border-white/10 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
                    placeholder="https://votre-portfolio.com"
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Motivation <span className="text-rose-500">*</span>
                  </label>
                  <textarea
                    name="motivation"
                    value={formData.motivation}
                    onChange={handleChange}
                    required
                    rows="6"
                    minLength="50"
                    className={`w-full px-4 py-3 rounded-lg border ${isDark ? 'bg-[#0f1114] border-white/10 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
                    placeholder="Parlez-nous de votre expérience, vos créations, et pourquoi vous souhaitez rejoindre Krayne Agency... (minimum 50 caractères)"
                  />
                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    {formData.motivation.length} / 50 caractères minimum
                  </p>
                </div>

                {/* Commission Info Box */}
                <div className={`p-6 rounded-lg ${isDark ? 'bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border border-yellow-500/30' : 'bg-gradient-to-br from-yellow-50 to-orange-50 border border-yellow-300'}`}>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-yellow-600" />
                    Répartition des revenus
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span>Vous (créateur)</span>
                      <span className="font-bold text-green-600 text-lg">75%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>URSSAF (charges sociales)</span>
                      <span className="font-bold text-red-600">20%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Krayne Agency (commission plateforme)</span>
                      <span className="font-bold text-yellow-600">5%</span>
                    </div>
                  </div>
                  <p className={`text-xs mt-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    💡 Sur chaque vente, vous recevez 75% du prix HT directement sur votre compte.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full px-6 py-4 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors font-medium text-lg flex items-center justify-center gap-2 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Envoyer ma demande
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DevenirCreateur;
